<template>
  <view class="message-row-container">
    <view class="message-row" :class="[position, 'no-avatar']">

      <!-- 文本/Mix 消息 -->
      <view 
        v-if="type === 'msg' || type === 'mix'" 
        class="message" 
        :class="[type, { streaming }]" 
        :style="{ backgroundColor: color }"
      >
        <!-- 内容块遍历 -->
        <template v-for="(block,) in contentBlocks" :key="block.id">

          <!-- 思考过程 -->
          <view v-if="block.type === 'reasoning'" class="block-wrapper reasoning-wrapper">
            <!-- 折叠状态：单行摘要 -->
            <view 
              v-if="!isExpanded(block.id)" 
              class="block-summary"
              :class="{ loading: block.isLoading || isBlockLoading(block.id) }"
              @click="toggleBlock(block.id)"
            >
              <view class="summary-left">
                <el-icon class="summary-icon"><Orange /></el-icon>
                <text class="summary-text">
                  {{ block.isLoading || isBlockLoading(block.id) ? '思考中...' : (block.summary || '思考过程') }}
                </text>
              </view>
              <view class="summary-right">
                <view v-if="block.isLoading || isBlockLoading(block.id)" class="mini-loader">
                  <view class="mini-dot"></view>
                  <view class="mini-dot"></view>
                  <view class="mini-dot"></view>
                </view>
                <el-icon class="expand-icon"><ArrowDown /></el-icon>
              </view>
            </view>

            <!-- 展开状态：完整内容 -->
            <view v-else class="block-detail reasoning-detail">
              <view class="detail-header" @click="toggleBlock(block.id)">
                <view class="header-left">
                  <el-icon><Orange /></el-icon>
                  <text>思考过程</text>
                </view>
                <el-icon class="collapse-icon"><ArrowUp /></el-icon>
              </view>
              <view class="detail-body">
                <pre style="max-height: none;">{{ block.content }}</pre>
              </view>

              <!-- 折叠栏（当内容展开时显示） -->
              <view 
                class="collapse-bar top"
                @click="toggleBlock(block.id)"
              >
                <el-icon><ArrowUp /></el-icon>
                <text>收起详情</text>
              </view>
            </view>
          </view>

          <!-- 工具调用开始 -->
          <view v-else-if="block.type === 'tool_start'" class="block-wrapper tool-wrapper">
            <view 
              v-if="!isExpanded(block.id)" 
              class="block-summary tool-start-summary"
              :class="{ loading: block.isLoading || isBlockLoading(block.id) }"
              @click="toggleBlock(block.id)"
            >
              <view class="summary-left">
                <el-icon class="summary-icon" :class="{ spin: block.isLoading || isBlockLoading(block.id) }"><Tools /></el-icon>
                <text class="summary-text">调用: {{ block.content }}</text>
              </view>
              <view class="summary-right">
                <view v-if="block.isLoading || isBlockLoading(block.id)" class="mini-loader">
                  <view class="mini-dot"></view>
                  <view class="mini-dot"></view>
                  <view class="mini-dot"></view>
                </view>
                <el-icon class="expand-icon"><ArrowDown /></el-icon>
              </view>
            </view>

            <view v-else class="block-detail tool-detail tool-start-detail">
              <view class="detail-header" @click="toggleBlock(block.id)">
                <view class="header-left">
                  <el-icon><Tools /></el-icon>
                  <text>正在调用: {{ block.content }}</text>
                </view>
                <el-icon class="collapse-icon"><ArrowUp /></el-icon>
              </view>
            </view>
          </view>

          <!-- 工具调用结束 -->
          <view v-else-if="block.type === 'tool_end'" class="block-wrapper tool-wrapper">
            <view 
              v-if="!isExpanded(block.id)" 
              class="block-summary tool-end-summary"
              @click="toggleBlock(block.id)"
            >
              <view class="summary-left">
                <el-icon class="summary-icon" :class="{ success: !block.isLoading && !isBlockLoading(block.id) }"><CircleCheck /></el-icon>
                <text class="summary-text">{{ block.content }} 完成</text>
              </view>
              <el-icon class="expand-icon"><ArrowDown /></el-icon>
            </view>

            <view v-else class="block-detail tool-detail tool-end-detail">
              <view class="detail-header" @click="toggleBlock(block.id)">
                <view class="header-left">
                  <el-icon class="success-icon"><CircleCheck /></el-icon>
                  <text>{{ block.content }} 完成</text>
                </view>
                <el-icon class="collapse-icon"><ArrowUp /></el-icon>
              </view>
              <view v-if="block.result" class="detail-body">
                <pre class="result-pre">{{ block.result }}</pre>
              </view>
            </view>
          </view>

          <!-- 【优化】工具事件 - 简单展示，超长才展开 -->
          <view v-else-if="block.type === 'tool'" class="block-wrapper tool-wrapper simple-block">
            <!-- 简略模式：始终显示首行 -->
            <view class="block-summary tool-summary simple-summary">
              <view class="summary-left">
                <el-icon class="summary-icon"><SetUp /></el-icon>
                <text class="summary-text simple-text">{{ getFirstLine(block.content) }}</text>
              </view>
              <view class="summary-right">
                <!-- 状态图标 -->
                <el-icon class="status-icon" :class="getBlockStatus(block)"><CircleCheck /></el-icon>
                <!-- 只有超长才显示展开按钮 -->
                <view 
                  v-if="shouldShowExpand(block)" 
                  class="expand-btn"
                  @click.stop="toggleBlock(block.id)"
                >
                  <el-icon class="expand-icon"><ArrowDown /></el-icon>
                </view>
              </view>
            </view>
            <!-- 虚化效果遮罩 -->
            <view v-if="shouldShowExpand(block) && !isExpanded(block.id)" class="fade-overlay"></view>

            <!-- 展开后的详情 -->
            <view v-if="isExpanded(block.id)" class="block-detail tool-detail">
              <view class="detail-header" @click="toggleBlock(block.id)">
                <view class="header-left">
                  <el-icon><SetUp /></el-icon>
                  <text>工具详情</text>
                </view>
                <el-icon class="collapse-icon"><ArrowUp /></el-icon>
              </view>
              <view class="detail-body">
                <pre>{{ block.content }}</pre>
              </view>
            </view>
          </view>

          <!-- 【优化】系统事件 - 简单展示，超长才展开 -->
          <view v-else-if="block.type === 'event'" class="block-wrapper event-wrapper simple-block">
            <!-- 简略模式：始终显示首行 -->
            <view class="block-summary event-summary simple-summary">
              <view class="summary-left">
                <el-icon class="summary-icon"><InfoFilled /></el-icon>
                <text class="summary-text simple-text">{{ getFirstLine(block.content) }}</text>
              </view>
              <view class="summary-right">
                <!-- 状态图标 -->
                <el-icon class="status-icon" :class="getBlockStatus(block)"><CircleCheck /></el-icon>
                <!-- 只有超长才显示展开按钮 -->
                <view 
                  v-if="shouldShowExpand(block)" 
                  class="expand-btn"
                  @click.stop="toggleBlock(block.id)"
                >
                  <el-icon class="expand-icon"><ArrowDown /></el-icon>
                </view>
              </view>
            </view>
            <!-- 虚化效果遮罩 -->
            <view v-if="shouldShowExpand(block) && !isExpanded(block.id)" class="fade-overlay"></view>

            <!-- 展开后的详情 -->
            <view v-if="isExpanded(block.id)" class="block-detail event-detail">
              <view class="detail-header" @click="toggleBlock(block.id)">
                <view class="header-left">
                  <el-icon><InfoFilled /></el-icon>
                  <text>系统详情</text>
                </view>
                <el-icon class="collapse-icon"><ArrowUp /></el-icon>
              </view>
              <view class="detail-body">
                <pre>{{ block.content }}</pre>
              </view>
            </view>
          </view>

          <!-- 工作流步骤 -->
          <view v-else-if="block.type === 'workflow' || block.type === 'step'" class="block-wrapper workflow-wrapper">
            <view 
              v-if="!isExpanded(block.id)" 
              class="block-summary workflow-summary"
              @click="toggleBlock(block.id)"
            >
              <view class="summary-left">
                <el-icon class="summary-icon"><Memo /></el-icon>
                <text class="summary-text">{{ block.type === 'workflow' ? '工作流' : '步骤' }}</text>
              </view>
              <el-icon class="expand-icon"><ArrowDown /></el-icon>
            </view>

            <view v-else class="block-detail workflow-detail">
              <view class="detail-header" @click="toggleBlock(block.id)">
                <view class="header-left">
                  <el-icon><Memo /></el-icon>
                  <text>{{ block.type === 'workflow' ? '工作流详情' : '步骤详情' }}</text>
                </view>
                <el-icon class="collapse-icon"><ArrowUp /></el-icon>
              </view>
              <view class="detail-body">
                <pre>{{ block.content }}</pre>
              </view>
            </view>
          </view>

          <!-- 普通文本/Markdown -->
          <view v-else-if="block.type === 'raw-text'" class="text-block raw-text">{{ content }}</view>
          <view v-else class="text-block markdown-body" v-html="renderBlock(block)"></view>
        </template>

        <!-- 流式加载指示器（全局） -->
        <view v-if="showStreamingIndicator" class="streaming-indicator">
          <view class="streaming-dot"></view>
          <view class="streaming-dot"></view>
          <view class="streaming-dot"></view>
        </view>

        <!-- 底部插槽 -->
        <view v-if="$slots.bottom" class="bottom-slot">
          <slot name="bottom"></slot>
        </view>
      </view>

      <!-- 加载中 -->
      <view v-else-if="type === 'loading'" class="message loading">
        <view class="loading-dots">
          <view class="dot"></view>
          <view class="dot"></view>
          <view class="dot"></view>
        </view>
      </view>

      <!-- 错误提示 -->
      <view v-else-if="type === 'error'" class="message error">
        <el-icon class="error-icon"><CircleClose /></el-icon>
        <text class="error-text">{{ displayContent }}</text>
      </view>
    </view>
  </view>
</template>

<script>
import MarkdownIt from 'markdown-it'
import hljs from 'highlight.js'
import 'highlight.js/styles/github.css'

import {
  ArrowUp,
  ArrowDown,
  Orange,
  Tools,
  CircleCheck,
  SetUp,
  InfoFilled,
  Memo,
  CircleClose
} from '@element-plus/icons-vue'

export default {
  name: 'MessageRow',

  components: {
    ArrowUp,
    ArrowDown,
    Orange,
    Tools,
    CircleCheck,
    SetUp,
    InfoFilled,
    Memo,
    CircleClose
  },

  props: {
    mid: { 
      type: [Number, String], 
      default: 0 
    },
    type: { 
      type: String, 
      default: 'msg',
      validator: (val) => ['msg', 'mix', 'loading', 'error'].includes(val)
    },
    content: { 
      type: [String, Array, Object], 
      default: '' 
    },
    time: { 
      type: String, 
      default: '' 
    },
    position: { 
      type: String, 
      default: 'left',
      validator: (val) => ['left', 'right'].includes(val)
    },
    color: { 
      type: String, 
      default: undefined 
    },
    streaming: { 
      type: Boolean, 
      default: false 
    },
    defaultExpand: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      md: null,
      // 【修复】使用对象存储展开状态，key为block.id
      expandedBlocks: {},
      // 【新增】内部loading状态管理 - 用于追踪正在追加内容的块
      internalLoadingMap: {},
      // 【新增】debounce计时器存储
      loadingTimers: {},
      // 【新增】记录上一次内容的哈希，用于检测变化
      contentHashMap: {},
      gloable_message_id: undefined
    }
  },

  computed: {
    // 统一转换为标准内容块数组 —— 纯计算，无副作用
    contentBlocks() {
      if (!this.content) return []

      let blocks = []

      if (Array.isArray(this.content)) {
        blocks = this.content.map((item, idx) => this.normalizeBlock(item, idx)).filter(Boolean)
      } else if (typeof this.content === 'string' && this.position == 'right') {
        blocks = [{ 
          id: `text_${this.mid}_$`,
          type: 'raw-text', 
          content: this.content 
        }]
      } else if (typeof this.content === 'string') {
        blocks = [{ 
          id: `text_${this.mid}_`,
          type: 'text', 
          content: this.content 
        }]
      } else if (typeof this.content === 'object' && this.content !== null) {
        const normalized = this.normalizeBlock(this.content, 0)
        blocks = normalized ? [normalized] : []
      }

      return blocks
    },

    // 【修复】添加缺失的 showStreamingIndicator
    showStreamingIndicator() {
      return this.streaming && this.hasLoadingBlock
    },

    hasCollapsibleBlocks() {
      // 防御：确保 contentBlocks 是数组
      const blocks = this.contentBlocks
      if (!Array.isArray(blocks)) return false
      return blocks.some(block => 
        ['reasoning', 'tool_start', 'tool_end', 'tool', 'event', 'workflow', 'step'].includes(block?.type)
      )
    },

    hasLoadingBlock() {
      return this.contentBlocks.some(block => {
        // 检查外部传入的isLoading + 内部检测的loading
        return block.isLoading || this.isBlockLoading(block.id)
      })
    },

    isAnyExpanded() {
      return Object.values(this.expandedBlocks).some(v => v === true)
    },

    displayContent() {
      if (!this.content) return ''
      if (typeof this.content === 'string') return this.content
      if (Array.isArray(this.content)) {
        return this.content
          .map(c => c.content || c.text || '')
          .filter(Boolean)
          .join(' ')
      }
      return String(this.content)
    }
  },

  watch: {
    contentBlocks: {
      immediate: true,
      handler(newBlocks, oldBlocks) {
        if (!Array.isArray(newBlocks)) return

        // 【核心修复】检测每个块的内容变化，管理内部loading状态
        newBlocks.forEach((block, idx) => {
          const blockId = block.id
          const oldBlock = oldBlocks && oldBlocks[idx]

          // 初始化展开状态（如果是新块且正在loading）
          const hasKey = Object.prototype.hasOwnProperty.call(this.expandedBlocks, blockId)
          if (!hasKey) {
            // 新块默认不展开，除非defaultExpand为true
            this.expandedBlocks[blockId] = this.defaultExpand || false
          }

          // 【核心逻辑】检测内容变化并管理loading状态
          if (block.type === 'reasoning' || block.type === 'tool_start' || block.type === 'tool') {
            this.detectContentChange(block, oldBlock)
          }

          // 如果外部标记isLoading为false且内容不再变化，确保内部loading也关闭
          if (block.isLoading === false && oldBlock && oldBlock.isLoading === true) {
            this.clearBlockLoading(blockId)
          }
        })
      }
    }
  },

  created() {
    this.initMarkdown()

    if (!window.$codes_message_index) window.$codes_message_index = 0
    this.gloable_message_id = window.$codes_message_index ++
    if (!window.$codes) window.$codes = []
    if (!window.$codes[this.gloable_message_id]) window.$codes[this.gloable_message_id] = []

    if (this.defaultExpand) {
      this.contentBlocks.forEach((block) => {
        this.expandedBlocks[block.id] = true
      })
    }
  },

  mounted() {
    this.emitSourceLoaded()
  },

  beforeUnmount() {
    // 【新增】清理所有计时器
    this.clearAllTimers()
  },

  methods: {
    // ==================== 【新增】内部Loading状态管理 ====================

    /**
     * 检测内容变化并管理loading状态
     * 当内容变化时：刷新loading状态 + 重置debounce计时器
     * 当1秒内无变化时：自动关闭loading
     */
    detectContentChange(newBlock, oldBlock) {
      const blockId = newBlock.id
      const newContent = newBlock.content || ''
      const oldContent = oldBlock ? (oldBlock.content || '') : ''

      // 计算内容哈希（简单字符串比较）
      const newHash = this.computeHash(newContent)
      const oldHash = oldBlock ? this.computeHash(oldContent) : null

      // 内容发生变化
      if (newHash !== oldHash) {
        // 记录新哈希
        this.contentHashMap[blockId] = newHash

        // 如果内容非空且正在流式接收（streaming=true），触发内部loading
        if (this.streaming && newContent.length > 0) {
          this.refreshBlockLoading(blockId)
        }
      }
    },

    /**
     * 刷新块的loading状态 - 重置debounce计时器
     */
    refreshBlockLoading(blockId) {
      // 先清除旧的计时器
      this.clearBlockTimer(blockId)

      // 标记为loading中
      this.internalLoadingMap[blockId] = true

      // 设置新的debounce计时器 - 1秒后关闭loading
      const timer = setTimeout(() => {
        this.internalLoadingMap[blockId] = false
        delete this.loadingTimers[blockId]

        // 如果此时该块是展开的，保持展开状态（用户可能正在看）
        // 如果未展开，自动展开让用户看到完整内容
        if (!this.expandedBlocks[blockId]) {
          this.expandedBlocks[blockId] = true
        }
      }, 1000)

      this.loadingTimers[blockId] = timer
    },

    /**
     * 清除指定块的计时器
     */
    clearBlockTimer(blockId) {
      if (this.loadingTimers[blockId]) {
        clearTimeout(this.loadingTimers[blockId])
        delete this.loadingTimers[blockId]
      }
    },

    /**
     * 清除指定块的loading状态
     */
    clearBlockLoading(blockId) {
      this.clearBlockTimer(blockId)
      this.internalLoadingMap[blockId] = false
    },

    /**
     * 清除所有计时器（组件卸载时）
     */
    clearAllTimers() {
      Object.keys(this.loadingTimers).forEach(blockId => {
        clearTimeout(this.loadingTimers[blockId])
      })
      this.loadingTimers = {}
      this.internalLoadingMap = {}
    },

    /**
     * 判断块是否处于内部loading状态
     */
    isBlockLoading(blockId) {
      return !!this.internalLoadingMap[blockId]
    },

    /**
     * 计算内容哈希（简单版）
     */
    computeHash(content) {
      if (!content) return ''
      const str = typeof content === 'string' ? content : String(content)
      // 使用长度+前20字符+后20字符作为简单哈希
      if (str.length <= 40) return str
      return str.length + '_' + str.slice(0, 20) + '_' + str.slice(-20)
    },

    // ==================== 原有方法（保持兼容） ====================

    initMarkdown() {
      this.md = new MarkdownIt({
        html: true,
        linkify: true,
        typographer: true,
        breaks: true,
        highlight: (str, lang) => {
          var index = window.$codes[this.gloable_message_id].indexOf(str)
          if (index == -1) {
            index = this.md.cur_tmp_index++
            window.$codes[this.gloable_message_id][index] = str
          }

          try {
            if (lang && hljs.getLanguage(lang)) {
              const highlighted = hljs.highlight(str, { 
                language: lang, 
                ignoreIllegals: true 
              }).value

              return this.renderCodeBlock(highlighted, lang, index)
            }
          } catch (err) {
            console.warn('Highlight error:', err)
          }

          return `<pre class="hljs"><code>${this.md.utils.escapeHtml(str)}</code></pre>`
        }
      })
      const render_proxy = this.md.render;
      this.md.render = (...args) => {
        this.md.cur_tmp_index = 0;
        return render_proxy.call(this.md, ...args);
      };
    },

    renderCodeBlock(highlightedCode, lang, index) {
      return `
        <pre class="hljs">
          <div class="code-tools" title="为了您的代码安全，需要您手动点击应用代码按钮，AI生成的代码才会生效哦~">
            <div class="lang">${this.md.utils.escapeHtml(lang)}</div>
            <div class="tools row center">
              <div class="tools row center">
                <div class="tool row" data-codeid="${this.gloable_message_id}:${index}" onclick="$bus.emit('use-code', {mid:${this.gloable_message_id}, cid:${index}})" title="应用代码"><img class="icon-btn" src="${require('@/assets/icons/component_download.png')}"></div>
                <div class="tool row" onclick="$bus.emit('copy-code', {mid:${this.gloable_message_id}, cid:${index}})" title="复制代码"><img class="icon-btn" src="${require('@/assets/icons/copy.png')}"></div>
              </div>
            </div>
          </div>
          <code class="language-${this.md.utils.escapeHtml(lang)}">${highlightedCode}</code>
        </pre>`
    },

    // 生成唯一 ID —— 纯函数，无副作用
    generateId(prefix, idx) {
      return `${prefix}_${this.mid}_${idx}}`
    },

    normalizeBlock(item, idx) {
      if (!item) return { 
        id: this.generateId('empty', idx), 
        type: 'text', 
        content: '' 
      }

      // 如果已经是标准格式，确保有所有必要字段
      if (item.id && item.type) {
        return {
          id: item.id,
          type: item.type,
          content: item.content || item.text || '',
          summary: item.summary || item.title,
          isLoading: !!item.isLoading || !!item.loading,
          result: item.result || null,
          autoCollapse: !!item.autoCollapse
        }
      }

      return {
        id: item.id || this.generateId(item.type || 'text', idx),
        type: item.type || 'text',
        content: item.content || item.text || String(item),
        summary: item.summary || item.title,
        isLoading: item.isLoading || item.loading || false,
        result: item.result || null,
        autoCollapse: item.autoCollapse || false
      }
    },

    // 【新增】判断是否为简单块类型（tool/event）
    isSimpleBlock(block) {
      return ['tool', 'event'].includes(block?.type)
    },

    // 【新增】判断是否应该显示展开按钮（内容超过100字）
    shouldShowExpand(block) {
      if (!block || !block.content) return false
      const content = typeof block.content === 'string' ? block.content : String(block.content)
      return content.length > 100
    },

    // 【新增】获取首行文本
    getFirstLine(content) {
      if (!content) return ''
      const str = typeof content === 'string' ? content : String(content)
      const firstLine = str.split('\n')[0].trim()
      // 如果首行太长，截断并添加省略号
      return firstLine.length > 80 ? firstLine.substring(0, 80) + '...' : firstLine
    },

    // 【新增】获取块状态样式
    getBlockStatus(block) {
      if (block.isLoading || this.isBlockLoading(block.id)) return 'status-loading'
      if (block.result) return 'status-success'
      return 'status-default'
    },

    // 【修复】使用block.id作为key，而不是idx
    toggleBlock(blockId) {
      const current = !!this.expandedBlocks[blockId]
      this.expandedBlocks[blockId] = !current
    },

    // 【修复】使用block.id作为key
    isExpanded(blockId) {
      return !!this.expandedBlocks[blockId]
    },

    collapseAll() {
      Object.keys(this.expandedBlocks).forEach(key => {
        this.expandedBlocks[key] = false
      })
    },

    renderBlock(block) {
      if (!block.content) return ''
      if (block.type === 'text' || !block.type) {
        return this.md.render(block.content)
      }
      return ''
    },

    emitSourceLoaded() {
      this.$nextTick(() => {
        this.$emit('sourceLoaded')
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.message-row-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  overflow: hidden;
  flex-shrink: 0;

  .message-row {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;

    &.left {
      &.no-avatar {
        .message {
          border-radius: 16px;
          border-top-left-radius: 4px;
          background-color: #ffffff;
          box-shadow: 0 2px 8px rgba(139, 92, 246, 0.08);
        }
      }
    }

    &.right {
      flex-direction: row-reverse;

      &.no-avatar {
        .message {
          border-radius: 16px;
          border-top-right-radius: 4px;
          background: linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%);
          color: #ffffff;
        }
      }
    }

    .message {
      margin-top: 20px;
      display: flex;
      flex-direction: column;
      max-width: 85%;
      word-break: break-word;

      &.msg, &.mix {
        padding: 12px 16px;
        font-size: 15px;
        line-height: 1.6;

        .collapse-bar {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 6px;
          margin: 4px 0;
          border-radius: 6px;
          cursor: pointer;
          color: #8b5cf6;
          font-size: 12px;
          transition: all 0.2s ease;
          user-select: none;

          &.bottom {
            margin-top: 8px;
            border-top: 1px dashed rgba(139, 92, 246, 0.15);
          }

          .el-icon {
            font-size: 14px;
          }
        }

        .raw-text {
          font-size: 14px; 
          font-family: 'Consolas', auto;
          white-space: pre-line;
        }

        .block-wrapper {
          margin: 4px 0;
          position: relative;

          // 【新增】简单块样式
          &.simple-block {
            .simple-summary {
              cursor: default;

              &:hover {
                background: inherit;
                border-color: inherit;
              }

              .simple-text {
                font-size: 13px;
                color: #6b7280;
              }

              .summary-right {
                display: flex;
                align-items: center;
                gap: 8px;

                .status-icon {
                  font-size: 14px;

                  &.status-success {
                    color: #10b981;
                  }

                  &.status-loading {
                    color: #f59e0b;
                    animation: spin 1.5s linear infinite;
                  }

                  &.status-default {
                    color: #9ca3af;
                  }
                }

                .expand-btn {
                  cursor: pointer;
                  padding: 2px 4px;
                  border-radius: 4px;
                  transition: all 0.2s ease;

                  &:hover {
                    background: rgba(139, 92, 246, 0.08);
                  }
                }
              }
            }

            // 【新增】虚化效果遮罩
            .fade-overlay {
              position: absolute;
              bottom: 0;
              left: 0;
              right: 40px;
              height: 20px;
              background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.9));
              pointer-events: none;
              border-radius: 0 0 8px 8px;
            }
          }

          .block-summary {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            padding: 8px 12px;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s ease;
            border: 1px solid transparent;
            user-select: none;

            .summary-left {
              display: flex;
              align-items: center;
              gap: 8px;
              flex: 1;
              min-width: 0;
              overflow: hidden;

              .summary-icon {
                font-size: 16px;
                flex-shrink: 0;
                color: #8b5cf6;
                transition: transform 0.3s ease;

                &.spin {
                  animation: spin 1.5s linear infinite;
                }

                &.success {
                  color: #10b981;
                }
              }

              .summary-text {
                font-size: 13px;
                color: #4b5563;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }

            .summary-right {
              display: flex;
              align-items: center;
              gap: 8px;
              flex-shrink: 0;

              .expand-icon {
                font-size: 14px;
                color: #9ca3af;
                transition: transform 0.2s ease;
              }
            }

            &:hover {
              background: rgba(139, 92, 246, 0.04);
              border-color: rgba(139, 92, 246, 0.1);

              .expand-icon {
                transform: translateY(1px);
              }
            }

            &.reasoning-summary {
              background: rgba(139, 92, 246, 0.03);
              border-color: rgba(139, 92, 246, 0.1);
            }

            &.tool-start-summary {
              background: #fffbeb;
              border-color: #f59e0b;

              .summary-icon { color: #f59e0b; }
            }

            &.tool-end-summary {
              background: #ecfdf5;
              border-color: #10b981;

              .summary-icon { color: #10b981; }
            }

            &.tool-summary {
              background: #f8f9fa;
              border-color: #d1d5db;
            }

            &.event-summary {
              background: #eff6ff;
              border-color: #3b82f6;

              .summary-icon { color: #3b82f6; }
            }

            &.workflow-summary {
              background: #eff6ff;
              border-color: #3b82f6;

              .summary-icon { color: #3b82f6; }
            }

            &.loading {
              .summary-text {
                color: #8b5cf6;
                font-weight: 500;
              }
            }
          }

          .block-detail {
            border-radius: 8px;
            overflow: hidden;
            border: 1px solid;
            animation: expandIn 0.25s ease;

            .detail-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 8px;
              padding: 10px 12px;
              cursor: pointer;
              user-select: none;
              transition: background 0.2s ease;

              .header-left {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 13px;
                font-weight: 500;

                .el-icon {
                  font-size: 16px;
                }

                .success-icon {
                  color: #10b981;
                }
              }

              .collapse-icon {
                font-size: 14px;
                color: #9ca3af;
                transition: all 0.2s ease;
              }

              &:hover {
                .collapse-icon {
                  transform: translateY(-1px);
                }
              }
            }

            .detail-body {
              padding: 0 12px 12px;

              pre {
                margin: 0;
                padding: 10px;
                background: rgba(0, 0, 0, 0.03);
                border-radius: 6px;
                font-size: 12px;
                line-height: 1.6;
                color: #4b5563;
                font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
                white-space: pre-wrap;
                word-break: break-word;
                max-height: 300px;
                overflow-y: auto;
              }

              .result-pre {
                background: #ecfdf5;
                color: #065f46;
              }
            }

            &.reasoning-detail {
              background: rgba(139, 92, 246, 0.02);
              border-color: rgba(139, 92, 246, 0.15);

              .detail-header {
                color: #8b5cf6;

                &:hover { background: rgba(139, 92, 246, 0.04); }
              }
            }

            &.tool-start-detail {
              background: #fffbeb;
              border-color: #f59e0b;

              .detail-header {
                color: #d97706;

                &:hover { background: #fef3c7; }
              }
            }

            &.tool-end-detail {
              background: #ecfdf5;
              border-color: #10b981;

              .detail-header {
                color: #059669;

                &:hover { background: #d1fae5; }
              }
            }

            &.tool-detail {
              background: #f8f9fa;
              border-color: #e5e7eb;

              .detail-header {
                color: #4b5563;

                &:hover { background: #f3f4f6; }
              }
            }

            &.event-detail {
              background: #eff6ff;
              border-color: #bfdbfe;

              .detail-header {
                color: #2563eb;

                &:hover { background: #dbeafe; }
              }
            }

            &.workflow-detail {
              background: #eff6ff;
              border-color: #bfdbfe;

              .detail-header {
                color: #2563eb;

                &:hover { background: #dbeafe; }
              }
            }
          }
        }

        .mini-loader {
          display: flex;
          gap: 3px;
          align-items: center;

          .mini-dot {
            width: 4px;
            height: 4px;
            border-radius: 50%;
            background: #8b5cf6;
            animation: miniPulse 1.4s infinite ease-in-out;

            &:nth-child(1) { animation-delay: -0.32s; }
            &:nth-child(2) { animation-delay: -0.16s; }
          }
        }

        .markdown-body {
          width: 100%;
          word-wrap: break-word;
          word-break: break-word;
          font-size: 14px;
          line-height: 1.7;
        }

        .streaming-indicator {
          display: flex;
          gap: 4px;
          margin-top: 8px;
          align-items: center;

          .streaming-dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #8b5cf6;
            animation: pulse 1.4s infinite ease-in-out;

            &:nth-child(1) { animation-delay: -0.32s; }
            &:nth-child(2) { animation-delay: -0.16s; }
          }
        }

        .bottom-slot {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px solid rgba(139, 92, 246, 0.1);
        }
      }

      &.loading {
        background-color: #ffffff;
        padding: 16px 24px;
        border-radius: 16px;

        .loading-dots {
          display: flex;
          gap: 6px;
          align-items: center;

          .dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #8b5cf6;
            animation: bounce 1.4s infinite ease-in-out;

            &:nth-child(1) { animation-delay: -0.32s; }
            &:nth-child(2) { animation-delay: -0.16s; }
          }
        }
      }

      &.error {
        background: #fee2e2;
        border: 1px solid #fca5a5;
        color: #dc2626;
        padding: 12px 16px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        gap: 8px;

        .error-icon {
          font-size: 18px;
          flex-shrink: 0;
        }

        .error-text {
          font-size: 14px;
        }
      }
    }
  }
}

@keyframes pulse {
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
}

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

@keyframes miniPulse {
  0%, 80%, 100% { opacity: 0.3; }
  40% { opacity: 1; }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes expandIn {
  from { 
    opacity: 0; 
    transform: translateY(-4px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
}
</style>

<style lang="scss">
.message-row .message .markdown-body {
  margin: 0 !important;

  p {
    margin: 0 0 8px 0;
    &:last-child { margin-bottom: 0; }
  }

  h1, h2, h3, h4, h5, h6 {
    margin: 12px 0 8px 0;
    font-weight: 600;
    line-height: 1.4;
  }
  h1 { font-size: 18px; }
  h2 { font-size: 16px; }
  h3 { font-size: 14px; }
  h4 { font-size: 13px; }
  h5 { font-size: 12px; }

  ul, ol {
    margin: 8px 0;
    padding-left: 24px;
  }
  li { margin: 4px 0; }

  pre {
    font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
    font-size: 13px;

    code {
      display: flex;
      flex-direction: column;
      align-items: stretch;
    }
  }

  .hljs {
    position: relative;
    background: #f6f8fa;
    color: #24292e;
    border-radius: 8px;
    max-height: 450px;
    overflow-y: auto;
    overflow-x: auto;
    margin: 8px 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    border: 1px solid #e1e4e8;

    &::-webkit-scrollbar { 
      height: 5px; 
      width: 5px;
    }
    &::-webkit-scrollbar-track { 
      background: transparent; 
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(139, 92, 246, 0.2);
      border-radius: 3px;

      &:hover { background: rgba(139, 92, 246, 0.3); }
    }

    .code-tools {
      position: sticky;
      top: 0;
      left: 0;
      z-index: 2;
      background-color: #f1ecf5;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      margin: 0;
      padding: 8px 12px;
      border-bottom: 1px solid #e1e4e8;

      .lang {
        font-size: 12px;
        color: #666;
        font-weight: 500;
        text-transform: uppercase;
      }

      .tools {
        display: flex;
        gap: 8px;
      }

      .tool {
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: all 0.2s ease;

        &:hover {
          background: rgba(139, 92, 246, 0.1);
        }

        .icon-btn {
          width: 16px;
          height: 16px;
          display: block;
          color: #666;
        }
      }
    }

    code {
      background: transparent;
      padding: 12px;
      color: inherit;
      font-size: 13px;
      line-height: 1.6;
      display: block;
    }
  }

  blockquote {
    border-left: 4px solid #8b5cf6;
    padding-left: 12px;
    margin: 8px 0;
    color: #666;
    font-style: italic;
  }

  a {
    color: #8b5cf6;
    text-decoration: underline;

    &:hover {
      color: #7c3aed;
    }
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 8px 0;
    font-size: 14px;
  }
  th, td {
    border: 1px solid #e5e7eb;
    padding: 8px;
    text-align: left;
  }
  th {
    background: rgba(139, 92, 246, 0.1);
    font-weight: 600;
  }

  img {
    max-width: 100%;
    border-radius: 8px;
    margin: 8px 0;
  }

  hr {
    border: none;
    border-top: 1px solid rgba(139, 92, 246, 0.2);
    margin: 16px 0;
  }
}

.message-row.right .message .markdown-body {
  code {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
  }
  pre {
    background: rgba(0, 0, 0, 0.3);
    color: #ffffff;
  }
  blockquote {
    border-left-color: rgba(255, 255, 255, 0.5);
    color: rgba(255, 255, 255, 0.9);
  }
  a {
    color: #ffffff;
    text-decoration: underline;
  }
  th {
    background: rgba(255, 255, 255, 0.2);
  }
  td {
    border-color: rgba(255, 255, 255, 0.3);
  }
  .hljs {
    background: rgba(0, 0, 0, 0.2);
    border-color: rgba(255, 255, 255, 0.1);

    .code-tools {
      background-color: rgba(0, 0, 0, 0.2);
      border-bottom-color: rgba(255, 255, 255, 0.1);
    }
  }
}
</style>